import { booleanAttribute, Component, HostBinding, Input } from '@angular/core';
import { iconWeight, usableIcons } from '../icon/icon.component';
/**
 * ButtonComponent is an Angular component that enhances button (`<button>`) elements with customizable styles and optional icons.
 *
 * ## Usage Examples
 *
 * ```html
 * <!-- Default button -->
 * <button vee-btn right icon="angleClosed" [class]="color">Test</button>
 *
 * <!-- Underlined style -->
 * <button vee-btn="underlined" right icon="angleClosed" [class]="color">Test</button>
 *
 * <!-- Filled style -->
 * <button vee-btn="filled" right icon="angleClosed" [class]="color">Test</button>
 *
 * <!-- Active state -->
 * <button vee-btn class="active" right icon="angleClosed" [class]="color">Test</button>
 *
 * <!-- Disabled state -->
 * <button vee-btn disabled right icon="angleClosed" [class]="color">Test</button>
 * ```
 *
 * ## Inputs
 * - `vee-btn`: Sets the button style (`'' | 'filled' | 'outlined' | 'underlined'`).
 * - `icon`: Name of the icon to display (from `usableIcons`).
 * - `disabled`: Disables the button when true.
 * - `right`: Positions the icon to the right.
 * - `left`: Positions the icon to the left.
 *
 * ## Host Bindings
 * Applies CSS classes based on the selected style, icon position, and disabled state.
 */

export type btnType = '' | 'filled' | 'outlined' | 'underlined';

@Component({
  selector: 'button [vee-btn]',
  templateUrl: './clickable.component.html',
  styleUrls: ['./clickable.component.scss'],
  standalone: false,
})
export class ButtonComponent {
  @Input('vee-btn') veeBtn?: btnType;
  @Input('icon') icon?: usableIcons;
  @Input('icon-weight') iconWeight: iconWeight = 'regular';
  @Input({ transform: booleanAttribute }) disabled: boolean = false;
  @Input({ transform: booleanAttribute }) right: boolean = false;
  @Input({ transform: booleanAttribute }) left: boolean = false;

  // Angular binds [disabled] to this Input instead of the native button
  // property, so it must be re-propagated or the button stays clickable.
  @HostBinding('attr.disabled')
  get disabledAttr(): string | null {
    return this.disabled ? '' : null;
  }

  @HostBinding('class')
  get hostClasses(): string {
    let iconClass = '';
    if (this.icon && this.right) {
      iconClass = 'vee-icon-right';
    } else if (this.icon && this.left) {
      iconClass = 'vee-icon-left';
    }
    return [
      this.veeBtn ? `vee-btn-${this.veeBtn}` : 'vee-btn-text',
      iconClass,
      this.disabled ? 'disabled' : '',
    ].join(' ');
  }
}
